/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/react-in-jsx-scope */
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import { Dayjs } from 'dayjs'

import {
  ALL_TSHIRT_SIZES,
  COUNTRIES,
  GENDERS,
  TSHIRT_SIZES,
} from '../../../constants/models'
import { isCategoryMultisports } from '../../../utils/ApiHelpers'
import FormDatePicker from '../elements/FormDatePicker'
import FormSelectInput from '../elements/FormSelectInput'
import FormTextInput from '../elements/FormTextInput'
import emergencyContact from '../emergencyContact/content'
import { NO_PASTE_PROPS } from '../utils/pasteProps'
import RelayEventInput from '../utils/RelayEventInput'
import shirtFieldInputs from '../utils/shirtFieldInputs'
import SubCategoryInput from '../utils/SubCategoryInput'

interface CategoryProps {
  id: string
  name: string
  priority: number
  disabled?: boolean
}

const tandemForm = ({ formDisabled, updateField }) => ({
  id: 'tandem-form-details',
  name: 'Tandem Participant Information',
  inputs: [
    {
      id: 'tandemName',
      size: { sm: 12, md: 6 },
      field: (
        <FormTextInput
          name="tandemName"
          label="Name"
          required
          autoComplete="new-tandemName"
          disabled={formDisabled}
        />
      ),
    },
    {
      id: 'tandemBirthdate',
      size: { sm: 12, md: 6 },
      field: (
        <FormDatePicker
          name="tandemBirthdate"
          label="Birthdate"
          required
          autoComplete="new-tandemBirthdate"
          placeholder="YYYY-MM-DD"
          disabled={formDisabled}
          onModified={(value: Dayjs) =>
            updateField('tandemBirthdate', value, true)
          }
        />
      ),
    },
    {
      id: 'tandemShirtSize',
      size: { sm: 12, md: 6 },
      field: (
        <FormSelectInput
          name="tandemShirtSize"
          label="Shirt size"
          autoComplete="new-tandemShirtSize"
          placeholder="Select Shirt Size"
          disabled={formDisabled}
          options={Object.entries(TSHIRT_SIZES).map(([key, value]) => ({
            value: key,
            text: value,
          }))}
          required
        />
      ),
    },
  ],
})

const ParticipantFormContent = ({
  values,
  updateField,
  formDisabled,
  categories,
  checkCurrentUser,
  handleCheckCurrentUser,
  handleCategoryChange,
  tandemInfo,
  hasAdditionalId,
  additionalIdFieldLabel,
  handleRelayEventChange,
  selectedRelayEvent,
  currentRelayEvent,
  firstParticipants,
  isOnsiteRegistration,
  subCategories,
  hasJersey,
  hasBibName,
  fullShirtDetails,
}) => {
  return [
    {
      id: 'category-form-details',
      name: 'Event',
      inputs: [
        {
          id: 'categoryId',
          size: { sm: 12, md: 6 },
          field: (
            <FormSelectInput
              name="categoryId"
              label="Category"
              autoComplete="new-categoryId"
              disabled={formDisabled}
              placeholder="Select Category"
              options={categories
                .sort((a: CategoryProps, b: CategoryProps) => {
                  if (!!a.priority && !b.priority) return -1
                  if (!!b.priority && !a.priority) return 1
                  return a.priority - b.priority
                })
                .map((category: CategoryProps) => ({
                  value: category.id,
                  text: category.name,
                  disabled: category.disabled,
                }))}
              onModified={handleCategoryChange}
              required
            />
          ),
        },
        {
          ...(isCategoryMultisports(values) && {
            id: 'relayEvent',
            size: { sm: 12, md: 6 },
            field: (
              <RelayEventInput
                values={values}
                currentRelayEvent={currentRelayEvent}
                selectedRelayEvent={selectedRelayEvent}
                formDisabled={formDisabled}
                onRelayEventChange={handleRelayEventChange}
              />
            ),
          }),
        },
        ...shirtFieldInputs({ key: 1, fullShirtDetails, formDisabled }),
        ...shirtFieldInputs({ key: 2, fullShirtDetails, formDisabled }),
        {
          ...(hasJersey &&
            values.relayEvent.includes('BIKE') && {
              id: 'jerseySize',
              size: { sm: 12, md: 6 },
              field: (
                <FormSelectInput
                  name="jerseySize"
                  label={`${
                    fullShirtDetails.shirtDetails?.JERSEY?.LABEL ||
                    'Cycling jersey'
                  } size`}
                  autoComplete="new-jerseySize"
                  placeholder={`Select ${
                    fullShirtDetails.shirtDetails?.JERSEY?.LABEL ||
                    'Cycling jersey'
                  } size`}
                  disabled={formDisabled}
                  // options={Object.entries(TSHIRT_SIZES).map(([key, value]) => ({
                  //   value: key,
                  //   text: value,
                  // }))}
                  options={
                    `JERSEY` in fullShirtDetails.shirtDetails &&
                    'SIZE_OPTIONS' in fullShirtDetails.shirtDetails.JERSEY
                      ? fullShirtDetails.shirtDetails.JERSEY.SIZE_OPTIONS.map(
                          (value: string) => ({
                            value,
                            text: ALL_TSHIRT_SIZES[value],
                          }),
                        )
                      : Object.entries(TSHIRT_SIZES).map(([key, value]) => ({
                          value: key,
                          text: value,
                        }))
                  }
                  required
                />
              ),
            }),
        },
        {
          id: 'teamName',
          size: { sm: 12, md: 6 },
          field: (
            <FormTextInput
              name="teamName"
              label="Team Name"
              autoComplete="new-teamName"
              disabled={formDisabled}
            />
          ),
        },
        {
          ...(hasBibName && {
            id: 'bibName',
            size: { sm: 12, md: 6 },
            field: (
              <FormTextInput
                name="bibName"
                label="Name in bib"
                autoComplete="new-bibName"
                disabled={formDisabled}
              />
            ),
          }),
        },

        {
          ...(subCategories &&
            Object.keys(subCategories).length > 0 && {
              id: 'subCategory',
              size: { sm: 12, md: 6 },
              field: (
                <SubCategoryInput
                  subCategories={subCategories}
                  formDisabled={formDisabled}
                />
              ),
            }),
        },
      ],
    },
    {
      id: 'participant-form-details',
      name: 'Participant',
      inputs: [
        {
          ...(!isOnsiteRegistration && {
            id: 'checkCurrentUser',
            size: 12,
            field: (
              <Typography variant="body2">
                <Checkbox
                  checked={checkCurrentUser}
                  onChange={handleCheckCurrentUser}
                  inputProps={{
                    'aria-label': 'primary checkbox',
                    name: 'checkCurrentUser',
                  }}
                />
                Add User as Participant
              </Typography>
            ),
          }),
        },
        {
          ...(hasAdditionalId && {
            id: 'additionalId',
            size: { sm: 12 },
            field: (
              <FormTextInput
                name="additionalId"
                label={additionalIdFieldLabel}
                autoComplete="new-additionalId"
                disabled={formDisabled}
              />
            ),
          }),
        },
        {
          ...(firstParticipants &&
            firstParticipants.NUMBER > firstParticipants.TOTAL &&
            firstParticipants.ADDITIONAL_INFO_LABEL && {
              id: 'firstParticipantInfo',
              size: { sm: 12 },
              field: (
                <FormTextInput
                  name="firstParticipantInfo"
                  label={firstParticipants.ADDITIONAL_INFO_LABEL}
                  autoComplete="new-firstParticipantInfo"
                  disabled={formDisabled}
                />
              ),
            }),
        },
        {
          id: 'firstName',
          size: { sm: 12, md: 6 },
          field: (
            <FormTextInput
              name="firstName"
              label="First Name"
              required
              autoComplete="new-firstName"
              disabled={formDisabled}
            />
          ),
        },
        {
          id: 'lastName',
          size: { sm: 12, md: 6 },
          field: (
            <FormTextInput
              name="lastName"
              label="Last Name"
              required
              autoComplete="new-lastName"
              disabled={formDisabled}
            />
          ),
        },
        {
          id: 'birthdate',
          size: { sm: 12, md: 4 },
          field: (
            <FormDatePicker
              name="birthdate"
              label="Birthdate"
              required
              autoComplete="new-birthdate"
              placeholder="YYYY-MM-DD"
              disabled={formDisabled}
              onModified={(value: Dayjs) =>
                updateField('birthdate', value, true)
              }
            />
          ),
        },
        {
          id: 'nationality',
          size: { sm: 12, md: 4 },
          field: (
            <FormSelectInput
              name="nationality"
              label="Nationality"
              autoComplete="new-nationality"
              placeholder="Select Nationality"
              disabled={formDisabled}
              options={Object.entries(COUNTRIES).map(([key, value]) => ({
                value: key,
                text: value,
              }))}
              required
            />
          ),
        },
        {
          id: 'gender',
          size: { sm: 12, md: 4 },
          field: (
            <FormSelectInput
              name="gender"
              label="Gender"
              autoComplete="new-gender"
              placeholder="Select Gender"
              disabled={formDisabled}
              options={Object.entries(GENDERS).map(([key, value]) => ({
                value: key,
                text: value,
              }))}
              required
            />
          ),
        },
        {
          id: 'address',
          size: { sm: 12, md: 12 },
          field: (
            <FormTextInput
              name="address"
              label="Home Address"
              required
              autoComplete="new-address"
              disabled={formDisabled}
            />
          ),
        },

        {
          id: 'phoneNumber',
          size: { sm: 12, md: 6 },
          field: (
            <FormTextInput
              name="phoneNumber"
              label="Phone Number"
              required
              autoComplete="new-phoneNumber"
              disabled={formDisabled}
              {...NO_PASTE_PROPS}
            />
          ),
        },
        {
          ...(!isOnsiteRegistration && {
            id: 'confirmPhoneNumber',
            size: { sm: 12, md: 6 },
            field: (
              <FormTextInput
                name="confirmPhoneNumber"
                label="Confirm Phone Number"
                required
                autoComplete="new-confirmPhoneNumber"
                disabled={formDisabled}
                {...NO_PASTE_PROPS}
              />
            ),
          }),
        },
        {
          ...(isOnsiteRegistration && {
            id: 'email',
            size: { sm: 12, md: 6 },
            field: (
              <FormTextInput
                name="email"
                label="Email Address"
                autoComplete="new-email"
                disabled={formDisabled}
                {...NO_PASTE_PROPS}
                onModified={(e) => {
                  updateField('email', e.target.value.toUpperCase().trim())
                }}
              />
            ),
          }),
        },
        // {
        //   ...(!isOnsiteRegistration && {
        //     id: 'confirmEmail',
        //     size: { sm: 12, md: 6 },
        //     field: (
        //       <FormTextInput
        //         name="confirmEmail"
        //         label="Confirm Email Address"
        //         autoComplete="new-confirmEmail"
        //         disabled={formDisabled}
        //         onCut={(e) => e.preventDefault()}
        //         onCopy={(e) => e.preventDefault()}
        //         onPaste={(e) => {
        //           e.preventDefault()
        //         }}
        //         onModified={(e) => {
        //           updateField(
        //             'confirmEmail',
        //             e.target.value.toUpperCase().trim(),
        //           )
        //         }}
        //       />
        //     ),
        //   }),
        // },
      ],
    },
    { ...(tandemInfo.isTandem && tandemForm({ formDisabled, updateField })) },
    {
      id: 'participant-emergency-form-details',
      name: 'Emergency Contact Person',
      inputs: [...emergencyContact({ isOnsiteRegistration, formDisabled })],
    },
  ]
}
export default ParticipantFormContent
